/**
 * One web file picker for chat (analysis upload + audio) (de-slop CHAT-12).
 *
 * The two copies this replaces treated "window got focus back and the input
 * is still empty after 500 ms" as a cancel and then ignored the `change`
 * event. On iOS/Android, picking from iCloud, Drive or Photos often fires
 * `change` well after focus returns, so the chosen file was dropped.
 *
 * Now only the input's own `cancel` event (Chrome 113+, Safari 16.4+,
 * Firefox 91+) counts as a cancel. A late `change` is always delivered. The
 * picker must be opened synchronously from the user's tap (no timers or
 * awaits before `open`), or Safari blocks `input.click()`.
 */
export interface WebFilePickerOptions {
  accept: string[];
  onFile: (file: File) => void;
  /** Called when the browser reports the dialog was dismissed. */
  onCancel?: () => void;
}

export interface WebFilePickerEnv {
  document?: Pick<Document, 'createElement' | 'body'> | null;
}

let lastInput: HTMLInputElement | null = null;

/**
 * Opens the browser file dialog. Returns false when there is no DOM (native),
 * so the caller can fall back to expo-document-picker.
 */
export function openWebFilePicker(
  { accept, onFile, onCancel }: WebFilePickerOptions,
  env: WebFilePickerEnv = { document: typeof document === 'undefined' ? null : document },
): boolean {
  const doc = env.document;
  if (!doc || !doc.body) return false;

  // A browser without the `cancel` event never tells us about a dismissed
  // dialog; drop the previous hidden input instead of letting them pile up.
  lastInput?.remove();

  const input = doc.createElement('input') as HTMLInputElement;
  input.type = 'file';
  input.accept = accept.join(',');
  input.style.display = 'none';
  lastInput = input;

  let handled = false;
  const finish = () => {
    handled = true;
    input.onchange = null;
    input.removeEventListener('cancel', handleCancel);
    input.remove();
    if (lastInput === input) lastInput = null;
  };
  function handleCancel() {
    if (handled) return;
    finish();
    onCancel?.();
  }

  input.addEventListener('cancel', handleCancel);
  input.onchange = () => {
    if (handled) return;
    const file = input.files?.[0];
    finish();
    if (file) onFile(file);
    else onCancel?.();
  };

  doc.body.appendChild(input);
  input.click();
  return true;
}
